import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { FormTemplateForm, formTemplateToInput } from "./-components/form-template-form";
import { FormTemplateList, type FormTemplateRow } from "./-components/form-template-list";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/form-templates")({
  component: FormTemplatesPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** The forms a job posting can ask for: a document the candidate signs, or a questionnaire they
 * answer partway through the pipeline. */
function FormTemplatesPage() {
  const canManage = useCanOrganizationWide("hiring:manage");
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FormTemplateRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FormTemplateRow | null>(null);

  const templates = useQuery(orpc.workforce.hiringTemplates.forms.list.queryOptions({ input: {} }));
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.hiringTemplates.forms.key() });

  const create = useMutation(
    orpc.workforce.hiringTemplates.forms.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Form template created");
        setCreating(false);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.workforce.hiringTemplates.forms.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Form template updated");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.workforce.hiringTemplates.forms.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Form template deleted");
        setDeleteTarget(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = templates.data ?? [];
  const toSign = rows.filter((row) => row.signingTemplateId).length;
  const questionnaires = rows.length - toSign;

  return (
    <>
      <PageHeader
        title="Forms"
        meta={
          rows.length > 0
            ? `${rows.length} defined, ${toSign} to sign, ${questionnaires} questionnaire${questionnaires === 1 ? "" : "s"}. A form with a document is something the candidate signs; one without is questions they answer mid-pipeline.`
            : "Asked mid-pipeline. Attach a document to make it something they sign."
        }
        actions={canManage ? <Button onClick={() => setCreating(true)}>New form</Button> : null}
      />

      {templates.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : templates.isError ? (
        <ReadFailed
          message="Could not load forms."
          error={templates.error}
          onRetry={() => void templates.refetch()}
          retrying={templates.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No forms yet"
          description="Create one and attach it to a job posting. Candidates and hiring staff each get their own fields in the document."
        />
      ) : (
        <FormTemplateList
          templates={rows}
          canManage={canManage}
          onEdit={setEditing}
          onDelete={setDeleteTarget}
        />
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New form</DialogTitle>
            <DialogDescription>
              Two kinds of form, and the choice cannot be changed later.
            </DialogDescription>
          </DialogHeader>
          <FormTemplateForm
            showKind
            pending={create.isPending}
            submitLabel="Create"
            onCancel={() => setCreating(false)}
            onSubmit={(values) =>
              create.mutate({
                ...formTemplateToInput(values),
                kind: values.kind ?? "signing",
              })
            }
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit form</DialogTitle>
            <DialogDescription>
              Update the name or status. Whether a form is signed or answered is fixed when it is
              created.
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <FormTemplateForm
              showKind={false}
              pending={update.isPending}
              submitLabel="Save changes"
              defaultValues={{
                name: editing.name,
                description: editing.description ?? "",
                isActive: editing.isActive,
              }}
              onCancel={() => setEditing(null)}
              onSubmit={(values) =>
                update.mutate({ formTemplateId: editing.id, ...formTemplateToInput(values) })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this form?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes &ldquo;{deleteTarget?.name}&rdquo; and its linked document
              template. Jobs that referenced it will stop using this form.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deleteTarget && remove.mutate({ formTemplateId: deleteTarget.id })}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
